<template>
  <el-container :class="['chat-layout-container', { 'sidebar-open-mobile': isSidebarOpenOnMobile }]">
    <el-aside
        :width="sidebarWidth"
        class="sidebar-aside"
        :class="{ 'is-collapsed': isSidebarCollapsed && !isMobile }"
    >
      <ChatSidebar
          :is-collapsed="isSidebarCollapsed && !isMobile"
          :history-items="historyItems"
          :selected-chat-id="currentChatId"
          :show-load-more="showLoadMoreButton"
          :is-loading-history="isLoadingHistory"
          @new-chat="handleNewChatButtonClick" @select-chat="selectChat"
          @rename-chat="handleRenameChat"
          @delete-chat="handleDeleteChat"
          @load-more="loadMoreHistory"
      />
      <el-button
          v-if="isMobile && isSidebarOpenOnMobile"
          type="text"
          icon="el-icon-close"
          circle
          size="medium"
          @click="isSidebarOpenOnMobile = false"
          class="mobile-sidebar-close-button"
      ></el-button>
    </el-aside>

    <el-container class="main-area-container">
      <div v-if="isMobile && isSidebarOpenOnMobile" class="mobile-overlay" @click="isSidebarOpenOnMobile = false"></div>

      <el-header class="header">
        <div class="header-content">

          <div class="header-left">
            <el-button
                type="text"
                :icon="isMobile ? 'el-icon-s-fold' : (isSidebarCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold')"
                circle
                size="medium"
                @click="toggleSidebar"
                class="sidebar-toggle-button tech-icon-btn"
            ></el-button>

            <div class="current-chat-title-wrapper" v-if="!isMobile">
              <span class="title-divider">|</span>
              <span class="current-chat-title" :title="currentChatTitle">
                {{ isNewUnsavedChat ? $t('chat.newChatDefaultTitleEmpty') : currentChatTitle }}
              </span>
            </div>
          </div>

          <div class="header-center brand-center">

            <div class="brand-row-top">
              <img :src="require('@/assets/logo.png')" class="brand-logo tech-logo-filter" alt="Logo" />
              <div class="brand-name">Proclink</div>
            </div>

            <div class="brand-slogan">AI BUSINESS ASSISTANT</div>

          </div>

          <div class="header-right">
            <div class="pc-actions" v-if="!isMobile">
              <div class="user-info">
                {{ $t('chat.welcome', { username: username }) }}
              </div>

              <div class="language-switcher">
                <el-dropdown @command="handleLanguageChange" trigger="click">
                  <span class="el-dropdown-link tech-dropdown-link">
                    {{ $t('language.switch') }}<i class="el-icon-arrow-down el-icon--right"></i>
                  </span>
                  <el-dropdown-menu slot="dropdown" class="tech-dropdown-menu">
                    <el-dropdown-item command="en">{{ $t('language.en') }}</el-dropdown-item>
                    <el-dropdown-item command="zh">{{ $t('language.zh') }}</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>

              <el-button type="text" @click="logout" class="tech-text-btn">{{ $t('chat.logout') }}</el-button>
            </div>

            <div class="mobile-actions" v-if="isMobile">
              <el-dropdown @command="handleMobileCommand" trigger="click">
                <span class="el-dropdown-link tech-icon-btn">
                  <i class="el-icon-more" style="transform: rotate(90deg); font-weight: bold;"></i>
                </span>
                <el-dropdown-menu slot="dropdown" class="tech-dropdown-menu mobile-menu-dropdown">
                  <el-dropdown-item command="lang_en" :class="{'is-active': $i18n.locale === 'en'}">
                    🇺🇸 English
                  </el-dropdown-item>
                  <el-dropdown-item command="lang_zh" :class="{'is-active': $i18n.locale === 'zh'}">
                    🇨🇳 中文
                  </el-dropdown-item>
                  <el-dropdown-item divided command="logout" style="color: #FF3355;">
                    <i class="el-icon-switch-button"></i> {{ $t('chat.logout') }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </div>

        </div>
      </el-header>

      <el-main class="main-content">
        <ChatWindow
            :messages="currentMessages"
            @send-message="handleSendMessage"
            :is-chat-selected="currentChatId !== null || isNewUnsavedChat"
            :is-loading-response="isLoadingResponse"
            :is-loading-messages="isLoadingMessages"
            :empty-state-title="isNewUnsavedChat ? $t('chat.window.newChatEmptyStateTitle') : $t('chat.window.emptyStateTitle')"
            :empty-state-subtitle="isNewUnsavedChat ? $t('chat.window.newChatEmptyStateSubtitle') : $t('chat.window.emptyStateSubtitle')"
            :enter-message-placeholder="$t('chat.window.placeholder')"
        />
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import ChatSidebar from '../components/ChatSidebar.vue';
import ChatWindow from '../components/ChatWindow.vue';
import languageMixin from '../mixins/languageMixin';
import api from '../api';

const HttpStatus = {
  SUCCESS: 200,
  CREATED: 201,
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
};

export default {
  name: 'ChatLayout',
  mixins: [languageMixin],
  components: {
    ChatSidebar,
    ChatWindow
  },
  data() {
    return {
      isSidebarCollapsed: false,
      isMobile: false,
      isSidebarOpenOnMobile: false,
      username: localStorage.getItem('email') || 'User',
      historyItems: [],
      historyPagination: {
        total: 0,
        pages: 0,
        current: 0,
        size: 10,
      },
      isLoadingHistory: false,
      currentChatId: null,
      currentMessages: [],
      isLoadingMessages: false,
      isLoadingResponse: false,
      isCreatingNewChat: false,
      isNewUnsavedChat: false,
    };
  },
  computed: {
    currentChatTitle() {
      const selectedItem = this.historyItems.find(item => item.id === this.currentChatId);
      if (!selectedItem) {
        return this.$t('chat.newChatDefaultTitleEmpty');
      }
      const lang = this.$i18n.locale;
      if (lang === 'zh' && selectedItem.titleZh) {
        return selectedItem.titleZh;
      }
      if (lang === 'en' && selectedItem.titleEn) {
        return selectedItem.titleEn;
      }
      return selectedItem.title;
    },
    sidebarWidth() {
      if (this.isMobile) {
        return '260px';
      } else {
        return this.isSidebarCollapsed ? '64px' : '260px';
      }
    },
    showLoadMoreButton() {
      return this.historyItems.length < this.historyPagination.total;
    }
  },
  methods: {
    // --- 核心业务逻辑 ---
    handleLanguageChange(command) {
      this.switchLanguage(command);
    },
    handleMobileCommand(command) {
      if (command === 'logout') {
        this.logout();
      } else if (command === 'lang_en') {
        this.switchLanguage('en');
      } else if (command === 'lang_zh') {
        this.switchLanguage('zh');
      }
    },
    checkIsMobile() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 768;
      // 状态重置逻辑，防止布局错乱
      if (!this.isMobile && wasMobile) {
        this.isSidebarOpenOnMobile = false;
        this.isSidebarCollapsed = false;
      }
      if (this.isMobile && !wasMobile) {
        this.isSidebarCollapsed = false;
        this.isSidebarOpenOnMobile = false;
      }
    },
    toggleSidebar() {
      if (this.isMobile) {
        this.isSidebarOpenOnMobile = !this.isSidebarOpenOnMobile;
      } else {
        this.isSidebarCollapsed = !this.isSidebarCollapsed;
      }
    },
    logout() {
      this.$confirm(this.$t('chat.logoutConfirm.message'), this.$t('chat.logoutConfirm.title'), {
        confirmButtonText: this.$t('chat.logoutConfirm.confirm'),
        cancelButtonText: this.$t('chat.logoutConfirm.cancel'),
        type: 'warning',
        customClass: 'tech-confirm-box'
      }).then(() => {
        localStorage.clear(); // 清除所有本地存储
        this.$router.push('/login');
      }).catch(() => {});
    },
    // --- 聊天数据逻辑 ---
    async fetchHistory(page, size) {
      if (this.isLoadingHistory) return;
      this.isLoadingHistory = true;
      try {
        const response = await api.getHistory(page, size);
        const res = response.data;
        if (res.code === HttpStatus.SUCCESS) {
          const historyPageData = res.data;
          if (page === 1) {
            this.historyItems = historyPageData.records || [];
          } else {
            this.historyItems = [...this.historyItems, ...(historyPageData.records || [])];
          }
          this.historyPagination = {
            total: historyPageData.total || 0,
            pages: historyPageData.pages || 0,
            current: historyPageData.current || page,
            size: historyPageData.size || size,
          };
          if (this.currentChatId === null && this.historyItems.length > 0 && !this.isNewUnsavedChat) {
            this.selectChat(this.historyItems[0].id);
          }
        } else {
          this.$message({ message: res.msg || 'Failed to fetch chat history.', type: 'error' });
        }
      } catch (error) {
        this.$message({ message: 'Failed to fetch chat history.', type: 'error' });
      } finally {
        this.isLoadingHistory = false;
      }
    },
    loadMoreHistory() {
      if (this.isLoadingHistory || !this.showLoadMoreButton) return;
      const nextPage = this.historyPagination.current + 1;
      this.fetchHistory(nextPage, this.historyPagination.size);
    },
    async fetchMessages(chatId) {
      if (this.isLoadingMessages) return;
      this.isLoadingMessages = true;
      this.currentMessages = [];
      try {
        const response = await api.getMessages(chatId);
        const res = response.data;
        if (res.code === HttpStatus.SUCCESS) {
          this.currentMessages = res.data || [];
        } else {
          this.$message({ message: res.msg || 'Failed to fetch messages.', type: 'error' });
          this.currentMessages = [];
        }
      } catch (error) {
        this.$message({ message: 'Failed to fetch messages.', type: 'error' });
        this.currentMessages = [];
      } finally {
        this.isLoadingMessages = false;
      }
    },
    async refreshCurrentMessages(chatId) {
      if (!chatId) return;
      try {
        const response = await api.getMessages(chatId);
        const res = response.data;
        if (res.code === HttpStatus.SUCCESS) {
          this.currentMessages = res.data || [];
        }
      } catch (error) { console.error(error); }
    },
    selectChat(chatId) {
      if (this.currentChatId === chatId && !this.isNewUnsavedChat) {
        if (this.currentMessages.length === 0 && !this.isLoadingMessages) {
          this.fetchMessages(chatId);
        }
        if (this.isMobile) this.isSidebarOpenOnMobile = false;
        return;
      }
      this.isNewUnsavedChat = false;
      this.currentChatId = chatId;
      this.currentMessages = [];
      this.fetchMessages(chatId);
      if (this.isMobile) this.isSidebarOpenOnMobile = false;
    },
    handleNewChatButtonClick() {
      this.$confirm(this.$t('chat.consent.message'), this.$t('chat.consent.title'), {
        confirmButtonText: this.$t('chat.consent.agree'),
        cancelButtonText: this.$t('chat.consent.disagree'),
        type: 'info',
        customClass: 'tech-confirm-box'
      }).then(() => {
        this.currentChatId = null;
        this.currentMessages = [];
        this.isNewUnsavedChat = true;
        this.isLoadingResponse = false;
        if (this.isMobile) this.isSidebarOpenOnMobile = false;
      }).catch(() => {});
    },
    async handleSendMessage(messageText) {
      if (!messageText.trim()) return;
      if (this.isLoadingResponse || this.isLoadingMessages) {
        this.$message({ message: 'Please wait...', type: 'warning' });
        return;
      }
      const userMessage = {
        id: Date.now(),
        sender: 'user',
        content: messageText,
        create_time: new Date().toISOString()
      };
      this.currentMessages.push(userMessage);
      this.isLoadingResponse = true;
      try {
        const response = await api.sendMessage(this.currentChatId, messageText);
        const res = response.data;
        if (res.code === HttpStatus.CREATED || res.code === HttpStatus.SUCCESS) {
          const isNewChat = this.isNewUnsavedChat;
          const newChatId = res.data && res.data.chatId ? res.data.chatId : this.currentChatId;
          if (newChatId && this.currentChatId !== newChatId) {
            this.currentChatId = newChatId;
            this.isNewUnsavedChat = false;
          }
          if (isNewChat) {
            await this.fetchHistory(1, this.historyPagination.size);
          }
          await this.refreshCurrentMessages(this.currentChatId);
        } else {
          this.$message({ message: res.msg || 'Failed.', type: 'error' });
          this.currentMessages.pop();
        }
      } catch (error) {
        this.$message({ message: 'Failed to send.', type: 'error' });
        this.currentMessages.pop();
      } finally {
        this.isLoadingResponse = false;
      }
    },
    handleRenameChat(chatId) {
      const item = this.historyItems.find(item => item.id === chatId);
      if (!item) return;
      const lang = this.$i18n.locale;
      let currentTitle = item.title;
      if (lang === 'zh' && item.titleZh) currentTitle = item.titleZh;
      else if (lang === 'en' && item.titleEn) currentTitle = item.titleEn;

      this.$prompt(this.$t('chat.actions.promptRename'), this.$t('chat.actions.rename'), {
        inputValue: currentTitle,
        confirmButtonText: this.$t('chat.actions.save'),
        cancelButtonText: this.$t('chat.actions.cancel'),
        inputPattern: /\S/,
        customClass: 'tech-confirm-box'
      }).then(({value}) => {
        api.renameChat(chatId, value).then(response => {
          if (response.data.code === HttpStatus.SUCCESS) {
            item.title = value; item.titleEn = value; item.titleZh = value;
            this.$message({ type: 'success', message: this.$t('chat.actions.renameSuccess', {title: value}) });
          }
        });
      }).catch(() => {});
    },
    handleDeleteChat(chatId) {
      this.$confirm(this.$t('chat.actions.confirmDelete'), this.$t('chat.actions.delete'), {
        confirmButtonText: this.$t('chat.actions.deleteButton'),
        cancelButtonText: this.$t('chat.actions.cancel'),
        type: 'warning',
        customClass: 'tech-confirm-box'
      }).then(() => {
        api.deleteChat(chatId).then(response => {
          if (response.data.code === HttpStatus.SUCCESS) {
            const index = this.historyItems.findIndex(i => i.id === chatId);
            if (index !== -1) {
              this.historyItems.splice(index, 1);
              this.historyPagination.total--;
              if (this.currentChatId === chatId) {
                this.currentChatId = null;
                this.currentMessages = [];
                if (this.historyItems.length > 0) this.selectChat(this.historyItems[0].id);
                else this.isNewUnsavedChat = true;
              }
            }
            this.$message({ type: 'success', message: this.$t('chat.actions.deleteSuccess') });
            this.fetchHistory(1, this.historyPagination.size);
          }
        });
      }).catch(() => {});
    },
    initializeHistory() {
      this.fetchHistory(1, this.historyPagination.size);
    },
  },
  created() {
    this.checkIsMobile();
    window.addEventListener('resize', this.checkIsMobile);
    this.initializeHistory();
    this.currentChatId = null;
    this.isNewUnsavedChat = false;
  },
  mounted() {
    this.checkIsMobile();
    window.addEventListener('resize', this.checkIsMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIsMobile);
  }
};
</script>

<style scoped>
.chat-layout-container {
  height: 100vh;
  display: flex;
  background-color: transparent;
}

.sidebar-aside {
  background-color: rgba(5, 11, 20, 0.85);
  backdrop-filter: blur(12px);
  transition: width 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), transform 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(0, 153, 255, 0.15);
  position: relative;
  flex-shrink: 0;
  box-shadow: 2px 0 20px rgba(0, 0, 0, 0.5);
}

.sidebar-aside.is-collapsed {
  width: 64px !important;
  transform: translateX(0);
}

.main-area-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.header {
  background-color: rgba(5, 11, 20, 0.8);
  backdrop-filter: blur(10px);
  color: var(--tech-text-main);
  padding: 0 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  height: 60px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(0, 153, 255, 0.15);
  z-index: 10;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  position: relative;
}

/* 1. 左侧区域 */
.header-left {
  display: flex;
  align-items: center;
  max-width: 35%; /* 关键：限制宽度，防止挤占中间 Logo */
}

/* 2. 中间品牌区域 (绝对定位居中) */
.brand-center {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column; /* 垂直排列 */
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  pointer-events: none; /* 关键：防止遮挡下方元素（如点击穿透） */
  z-index: 1; /* 确保层级 */
}

/* 第一行: Logo + 名字 */
.brand-row-top {
  display: flex;
  align-items: center;
  margin-bottom: 0px;
}

/* 3. 右侧区域 */
.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* --- 品牌样式细节 --- */
.brand-logo {
  height: 24px; /* 适配两行布局 */
  width: auto;
  margin-right: 8px;
}
.tech-logo-filter {
  filter: brightness(0) invert(1) drop-shadow(0 0 2px rgba(0, 153, 255, 0.5));
}

.brand-name {
  font-size: 18px;
  font-weight: 700;
  color: var(--tech-text-main);
  letter-spacing: 1px;
  font-family: 'Segoe UI', sans-serif;
  line-height: 1;
}

.brand-slogan {
  font-size: 8px;
  color: var(--tech-primary);
  letter-spacing: 1.5px;
  font-weight: 500;
  margin-top: 2px;
  line-height: 1;
}

/* 标题样式 */
.current-chat-title-wrapper {
  display: flex;
  align-items: center;
  overflow: hidden;
}
.title-divider {
  margin: 0 10px;
  color: rgba(0, 153, 255, 0.3);
  font-weight: 300;
  flex-shrink: 0;
}
.current-chat-title {
  font-size: 15px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--tech-text-main);
  letter-spacing: 0.5px;
}

/* 按钮与操作区 */
.pc-actions, .mobile-actions {
  display: flex;
  align-items: center;
}

.user-info {
  margin-right: 20px;
  font-size: 14px;
  color: var(--tech-text-sub);
  white-space: nowrap;
}

.language-switcher {
  margin-right: 20px;
}

.tech-icon-btn {
  color: var(--tech-primary) !important;
  font-size: 20px;
  text-shadow: 0 0 5px rgba(0, 153, 255, 0.3);
  transition: all 0.3s ease;
}
.tech-icon-btn:hover {
  color: #fff !important;
  text-shadow: 0 0 10px var(--tech-primary);
}

.tech-dropdown-link {
  color: var(--tech-primary);
  font-size: 13px;
  letter-spacing: 1px;
}
.tech-dropdown-link:hover {
  color: #fff;
  text-shadow: 0 0 8px var(--tech-primary);
}

.tech-text-btn {
  color: var(--tech-text-sub) !important;
  font-size: 14px;
}
.tech-text-btn:hover {
  color: var(--tech-error) !important;
}

.main-content {
  padding: 0;
  background-color: transparent !important;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
}

.is-active {
  color: var(--tech-primary) !important;
  font-weight: bold;
  background-color: rgba(0, 153, 255, 0.1);
}

.mobile-sidebar-close-button {
  display: none;
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 101;
  background-color: transparent !important;
  border: none !important;
  color: var(--tech-text-main) !important;
  font-size: 20px !important;
}

.mobile-overlay {
  display: none;
}

@media (max-width: 768px) {
  .chat-layout-container { flex-direction: row; }
  .sidebar-aside {
    position: fixed;
    top: 0; left: 0; bottom: 0;
    height: 100vh;
    z-index: 100;
    width: 260px !important;
    transform: translateX(-100%);
    background-color: #050B14;
  }
  .chat-layout-container.sidebar-open-mobile .sidebar-aside { transform: translateX(0); }
  .mobile-sidebar-close-button { display: block !important; }
  .mobile-overlay {
    display: block;
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(3px);
    z-index: 99;
  }
}
</style>